import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { toast } from "react-toastify";

import DistributionGridApi from "../../../../../infrastructure/api/distributionGrid";
import DeleteConfirm from "../../../../components/shared/Dialog/DeleteConfirm";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import EnhancedTable from "../../../../components/shared/table";
import { canManageDistributionTeam } from "../../../../utils/authRoleHelpers";
import EDCBreadcrumbs, { EDC_MANAGEMENT_CRUMB, EDC_ROOT_CRUMB } from "../../EDCBreadcrumbs";
import EDCFormDialog from "../../EDCFormDialog";
import EDCListCardHeader from "../../EDCListCardHeader";
import { EDC_PATHS } from "../../edcPaths";
import useDistributionPortalContext, { pick } from "../../useDistributionPortalContext";
import {
  FEEDER_KINDS,
  OWNERSHIP_KINDS,
  TRANS_NUMBERS,
  listOf,
  runDelete,
  toApiDate,
  toDateValue,
  toFieldValue,
  toNumberOrNull,
  useGridLookups,
} from "../grid/gridShared";

const PERCENT_FIELDS = [
  { key: "percentHouseholds", label: "خانگی" },
  { key: "percentIndustrial", label: "صنعتی" },
  { key: "percentAgriculture", label: "کشاورزی" },
  { key: "percentPublic", label: "عمومی" },
  { key: "percentOther", label: "سایر" },
];

const emptyFeeder = {
  id: null,
  substationId: null,
  transNumber: 1,
  name: "",
  igmcCode: "",
  gisCode: "",
  distributionAreaId: "",
  feederKind: 1,
  ownershipKind: 0,
  cityId: null,
  maxRange: "",
  minRange: "",
  percentHouseholds: "",
  percentIndustrial: "",
  percentAgriculture: "",
  percentPublic: "",
  percentOther: "",
  actDate: null,
  dismandDate: null,
};

const toForm = (f) => ({
  id: pick(f, "id", "Id"),
  substationId: pick(f, "substationId", "SubstationId") ?? null,
  transNumber: Number(pick(f, "transNumber", "TransNumber") || 1),
  name: pick(f, "name", "Name") || "",
  igmcCode: pick(f, "igmcCode", "IgmcCode") || "",
  gisCode: pick(f, "gisCode", "GisCode") || "",
  distributionAreaId: toFieldValue(pick(f, "distributionAreaId", "DistributionAreaId")),
  feederKind: Number(pick(f, "feederKind", "FeederKind") ?? 1),
  ownershipKind: Number(pick(f, "ownershipKind", "OwnershipKind") ?? 0),
  cityId: pick(f, "cityId", "CityId") ?? null,
  maxRange: toFieldValue(pick(f, "maxRange", "MaxRange")),
  minRange: toFieldValue(pick(f, "minRange", "MinRange")),
  percentHouseholds: toFieldValue(pick(f, "percentHouseholds", "PercentHouseholds")),
  percentIndustrial: toFieldValue(pick(f, "percentIndustrial", "PercentIndustrial")),
  percentAgriculture: toFieldValue(pick(f, "percentAgriculture", "PercentAgriculture")),
  percentPublic: toFieldValue(pick(f, "percentPublic", "PercentPublic")),
  percentOther: toFieldValue(pick(f, "percentOther", "PercentOther")),
  actDate: toDateValue(pick(f, "actDate", "ActDate")),
  dismandDate: toDateValue(pick(f, "dismandDate", "DismandDate")),
});

const percentTotal = (form) =>
  PERCENT_FIELDS.reduce((sum, p) => sum + (toNumberOrNull(form[p.key]) || 0), 0);

const validateForm = (form) => {
  if (!form.substationId) return "ایستگاه فوق توزیع فیدر را انتخاب کنید";
  if (!String(form.name || "").trim()) return "نام فیدر الزامی است";
  const percents = PERCENT_FIELDS.map((p) => toNumberOrNull(form[p.key]) || 0);
  if (percents.some((p) => p < 0 || p > 100) || percentTotal(form) > 100)
    return "درصدهای تعرفه باید بین ۰ تا ۱۰۰ و مجموع آن‌ها حداکثر ۱۰۰ باشد";
  const max = toNumberOrNull(form.maxRange) || 0;
  const min = toNumberOrNull(form.minRange) || 0;
  if (min < 0 || max < 0 || (max > 0 && min > max)) return "حداقل و حداکثر بار مجاز فیدر نامعتبر است";
  return null;
};

const EDCFeedersPage = () => {
  const { context, companyId, companyName, isCompanyAdmin, setSelectedCompanyId, loading, error } =
    useDistributionPortalContext();
  const { lookups, reloadLookups } = useGridLookups(companyId, isCompanyAdmin);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [areaFilter, setAreaFilter] = useState("");
  const [substationFilter, setSubstationFilter] = useState("");
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadItems = useCallback(async () => {
    if (!companyId || !isCompanyAdmin) {
      setItems([]);
      return;
    }
    setItemsLoading(true);
    try {
      setItems(listOf(await DistributionGridApi.listFeeders(companyId)));
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت فیدرها");
    } finally {
      setItemsLoading(false);
    }
  }, [companyId, isCompanyAdmin]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const filterSubstations = useMemo(
    () =>
      areaFilter
        ? lookups.substations.filter((s) => String(s.parentId ?? "") === areaFilter)
        : lookups.substations,
    [lookups.substations, areaFilter]
  );

  const rows = useMemo(
    () =>
      items
        .map((f) => ({
          raw: f,
          id: pick(f, "id", "Id"),
          name: pick(f, "name", "Name") || "—",
          substationId: pick(f, "substationId", "SubstationId"),
          substationName: pick(f, "substationName", "SubstationName") || "",
          transTitle: `T${pick(f, "transNumber", "TransNumber") || "?"}`,
          distributionAreaId: pick(f, "distributionAreaId", "DistributionAreaId"),
          areaName: pick(f, "distributionAreaName", "DistributionAreaName") || "",
          feederKindTitle: pick(f, "feederKindTitle", "FeederKindTitle") || "",
          ownershipKindTitle: pick(f, "ownershipKindTitle", "OwnershipKindTitle") || "",
          cityName: pick(f, "cityName", "CityName") || "",
          lvTransCount: Number(pick(f, "lvTransCount", "LVTransCount", "LvTransCount") || 0),
          actDate: pick(f, "actDate", "ActDate"),
        }))
        .filter((r) => {
          if (areaFilter && String(r.distributionAreaId ?? "") !== areaFilter) return false;
          if (substationFilter && String(r.substationId) !== substationFilter) return false;
          return true;
        }),
    [items, areaFilter, substationFilter]
  );

  const headCells = [
    { id: "name", label: "نام فیدر" },
    { id: "substationName", label: "ایستگاه", render: (v) => v || "—" },
    { id: "transTitle", label: "ترانس قدرت" },
    { id: "areaName", label: "امور", render: (v) => v || "—" },
    { id: "feederKindTitle", label: "نوع", type: "chip" },
    { id: "ownershipKindTitle", label: "مالکیت" },
    { id: "lvTransCount", label: "ترانس‌های توزیع" },
    { id: "actDate", label: "شروع بهره‌برداری", type: "date" },
  ];

  const setField = (field, value) => setDialog((s) => ({ ...s, [field]: value }));

  const selectSubstation = (substation) =>
    setDialog((s) => ({
      ...s,
      substationId: substation?.id ?? null,
      distributionAreaId:
        !s.distributionAreaId && substation?.parentId != null ? String(substation.parentId) : s.distributionAreaId,
    }));

  const save = async () => {
    if (!companyId || !dialog) return;
    const validation = validateForm(dialog);
    if (validation) {
      toast.error(validation);
      return;
    }
    setSaving(true);
    try {
      await DistributionGridApi.saveFeeder({
        id: dialog.id || 0,
        distributionCompanyId: Number(companyId),
        distributionAreaId: toNumberOrNull(dialog.distributionAreaId),
        substationId: dialog.substationId,
        transNumber: Number(dialog.transNumber) || 1,
        name: dialog.name.trim(),
        igmcCode: dialog.igmcCode?.trim() || null,
        gisCode: dialog.gisCode?.trim() || null,
        feederKind: Number(dialog.feederKind) || 0,
        ownershipKind: Number(dialog.ownershipKind) || 0,
        cityId: dialog.cityId || null,
        maxRange: toNumberOrNull(dialog.maxRange) ?? 0,
        minRange: toNumberOrNull(dialog.minRange) ?? 0,
        ...Object.fromEntries(PERCENT_FIELDS.map((p) => [p.key, toNumberOrNull(dialog[p.key]) ?? 0])),
        actDate: toApiDate(dialog.actDate),
        dismandDate: toApiDate(dialog.dismandDate),
      });
      toast.success(dialog.id ? "تغییرات فیدر ذخیره شد" : "فیدر ایجاد شد");
      setDialog(null);
      await Promise.all([loadItems(), reloadLookups()]);
    } catch (e) {
      toast.error(e?.message || "خطا در ذخیره فیدر");
    } finally {
      setSaving(false);
    }
  };

  const refresh = () => Promise.all([loadItems(), reloadLookups()]);

  if (!canManageDistributionTeam()) return <Navigate to={EDC_PATHS.root} replace />;
  if (context && !isCompanyAdmin) return <Navigate to={EDC_PATHS.root} replace />;

  const busy = loading || itemsLoading;
  const selectedSubstation = lookups.substations.find((s) => s.id === dialog?.substationId) || null;
  const selectedCity = lookups.cities.find((c) => c.id === dialog?.cityId) || null;
  const dialogPercentTotal = dialog ? percentTotal(dialog) : 0;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <EDCBreadcrumbs
          crumbs={[EDC_ROOT_CRUMB, EDC_MANAGEMENT_CRUMB, { title: "فیدرها", to: EDC_PATHS.feeders }]}
        />
      </Grid>

      {error ? (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      ) : null}

      <Grid item xs={12}>
        <Card>
          <EDCListCardHeader
            title="فیدرهای فشار متوسط"
            subheader={
              companyName ? `${companyName} — ${items.length} فیدر` : "فیدرهای ۲۰ کیلوولت خروجی ایستگاه‌ها"
            }
            context={context}
            onCompanyChange={setSelectedCompanyId}
            onCreate={() => setDialog({ ...emptyFeeder })}
            createDisabled={!companyId || lookups.substations.length === 0}
            onRefresh={refresh}
            busy={busy}
          />
          <CardContent>
            {companyId && !loading && lookups.substations.length === 0 ? (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button component={RouterLink} to={EDC_PATHS.substations} size="small">
                    تعریف ایستگاه
                  </Button>
                }
              >
                هر فیدر باید به یک ایستگاه فوق توزیع متصل باشد؛ ابتدا ایستگاه‌ها را تعریف کنید.
              </Alert>
            ) : null}

            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
              <TextField
                select
                size="small"
                label="امور"
                value={areaFilter}
                onChange={(e) => {
                  setAreaFilter(e.target.value);
                  setSubstationFilter("");
                }}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">همه امور</MenuItem>
                {lookups.areas.map((a) => (
                  <MenuItem key={a.id} value={String(a.id)}>
                    {a.title}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="ایستگاه"
                value={substationFilter}
                onChange={(e) => setSubstationFilter(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">همه ایستگاه‌ها</MenuItem>
                {filterSubstations.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.title}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <EnhancedTable
              rowKey="id"
              rows={rows}
              headCells={headCells}
              pageSize={100}
              loading={busy}
              defaultSort={{ orderBy: "name", order: "asc" }}
              actions={[
                {
                  icon: <EditIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "ویرایش",
                  onClick: (row) => setDialog(toForm(row.raw)),
                },
                {
                  icon: <DeleteIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "حذف",
                  color: "error",
                  onClick: (row) => setDeleteTarget(row),
                },
              ]}
            />
          </CardContent>
        </Card>
      </Grid>

      <EDCFormDialog
        open={Boolean(dialog)}
        title={dialog?.id ? "ویرایش فیدر" : "ایجاد فیدر"}
        saving={saving}
        onClose={() => setDialog(null)}
        onSave={save}
      >
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={700}>
            اتصال به شبکه
          </Typography>
        </Grid>
        <Grid item xs={12} md={5}>
          <Autocomplete
            options={lookups.substations}
            value={selectedSubstation}
            getOptionLabel={(o) => o.title}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, v) => selectSubstation(v)}
            noOptionsText="ایستگاهی یافت نشد"
            renderInput={(params) => <TextField {...params} label="ایستگاه فوق توزیع" required />}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            select
            label="ترانس قدرت ایستگاه"
            value={dialog?.transNumber ?? 1}
            onChange={(e) => setField("transNumber", Number(e.target.value))}
            fullWidth
            required
          >
            {TRANS_NUMBERS.map((n) => (
              <MenuItem key={n} value={n}>{`T${n}`}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="امور"
            value={dialog?.distributionAreaId ?? ""}
            onChange={(e) => setField("distributionAreaId", e.target.value)}
            fullWidth
            helperText="خالی = امور ایستگاه"
          >
            <MenuItem value="">بدون امور</MenuItem>
            {lookups.areas.map((a) => (
              <MenuItem key={a.id} value={String(a.id)}>
                {a.title}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            مشخصات فیدر
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="نام فیدر"
            value={dialog?.name || ""}
            onChange={(e) => setField("name", e.target.value)}
            required
            fullWidth
            inputProps={{ maxLength: 50 }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="نوع فیدر"
            value={dialog?.feederKind ?? 1}
            onChange={(e) => setField("feederKind", Number(e.target.value))}
            fullWidth
          >
            {FEEDER_KINDS.map((k) => (
              <MenuItem key={k.value} value={k.value}>
                {k.title}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            select
            label="نوع مالکیت"
            value={dialog?.ownershipKind ?? 0}
            onChange={(e) => setField("ownershipKind", Number(e.target.value))}
            fullWidth
          >
            {OWNERSHIP_KINDS.map((k) => (
              <MenuItem key={k.value} value={k.value}>
                {k.title}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={4}>
          <Autocomplete
            options={lookups.cities}
            value={selectedCity}
            getOptionLabel={(o) => o.title}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, v) => setField("cityId", v?.id ?? null)}
            noOptionsText="شهری یافت نشد"
            renderInput={(params) => (
              <TextField {...params} label="شهر" helperText="خالی = شهر ایستگاه" />
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="کد IGMC"
            value={dialog?.igmcCode || ""}
            onChange={(e) => setField("igmcCode", e.target.value)}
            fullWidth
            inputProps={{ dir: "ltr" }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="کد GIS"
            value={dialog?.gisCode || ""}
            onChange={(e) => setField("gisCode", e.target.value)}
            fullWidth
            inputProps={{ dir: "ltr" }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="کمترین حد مجاز (MW)"
            type="number"
            value={dialog?.minRange ?? ""}
            onChange={(e) => setField("minRange", e.target.value)}
            fullWidth
            inputProps={{ min: 0, dir: "ltr" }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="بیشترین حد مجاز (MW)"
            type="number"
            value={dialog?.maxRange ?? ""}
            onChange={(e) => setField("maxRange", e.target.value)}
            fullWidth
            inputProps={{ min: 0, dir: "ltr" }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            درصد تعرفه مشترکین — مجموع: {dialogPercentTotal.toLocaleString("fa-IR")}٪
          </Typography>
        </Grid>
        {PERCENT_FIELDS.map((p) => (
          <Grid item xs={6} md={2.4} key={p.key}>
            <TextField
              label={p.label}
              type="number"
              value={dialog?.[p.key] ?? ""}
              onChange={(e) => setField(p.key, e.target.value)}
              fullWidth
              error={dialogPercentTotal > 100}
              inputProps={{ min: 0, max: 100, step: "any", dir: "ltr" }}
            />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            بهره‌برداری
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <JalaliDatePicker
            label="تاریخ شروع بهره‌برداری"
            value={dialog?.actDate || null}
            onChange={(v) => setField("actDate", toDateValue(v))}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <JalaliDatePicker
            label="تاریخ پایان بهره‌برداری"
            value={dialog?.dismandDate || null}
            onChange={(v) => setField("dismandDate", toDateValue(v))}
            fullWidth
          />
        </Grid>
      </EDCFormDialog>

      <DeleteConfirm
        open={Boolean(deleteTarget)}
        message={`فیدر «${deleteTarget?.name || ""}» حذف شود؟ فیدری که ترانس توزیع دارد قابل حذف نیست.`}
        onClose={(loadAgain) => {
          setDeleteTarget(null);
          if (loadAgain === true) refresh();
        }}
        onSubmit={() =>
          runDelete(
            () => DistributionGridApi.deleteFeeder({ distributionCompanyId: Number(companyId), id: deleteTarget.id }),
            "فیدر حذف شد"
          )
        }
      />
    </Grid>
  );
};

export default EDCFeedersPage;
